/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Calendar as CalendarIcon,
  Download,
  Search,
  Eye,
  X,
  CalendarDays,
  List,
} from 'lucide-react';
import axiosClient from '../api/axiosClient';
import toast from 'react-hot-toast';

export default function BookingsManager() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'calendar'
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, [statusFilter, search]);

  const fetchBookings = async () => {
    try {
      const res = await axiosClient.get(`/bookings?status=${statusFilter}&search=${search}`);
      setBookings(res.data);
    } catch (err) {
      toast.error('Failed to load bookings.');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axiosClient.put(`/bookings/${id}`, { status });
      toast.success(`Booking status updated to ${status}`);
      fetchBookings();
      if (selectedBooking && selectedBooking.id === id) {
        setSelectedBooking(prev => ({ ...prev, status }));
      }
    } catch (err) {
      toast.error('Failed to update booking status.');
    }
  };

  const handleExportCsv = () => {
    const csvContent = 'data:text/csv;charset=utf-8,' +
      ['Full Name,Email,Phone,Company,Service,Preferred Date,Status,Created At']
        .concat(bookings.map(b => `"${b.fullName}","${b.email}","${b.phone || ''}","${b.company || ''}","${b.serviceRequested || ''}","${b.preferredDate || ''}","${b.status}","${b.createdAt}"`))
        .join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `fajtech_bookings_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Bookings exported to CSV.');
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight flex items-center space-x-2">
            <CalendarIcon className="text-blue-500" />
            <span>Consultation Bookings</span>
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Track client consultation appointments, manage statuses, and review requests.
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* View mode toggle */}
          <div className="flex items-center bg-slate-900 border border-slate-800 p-1 rounded-xl">
            <button
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded-lg text-xs font-semibold flex items-center space-x-1 transition-all ${
                viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              <List size={14} />
              <span className="hidden sm:inline">Table</span>
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-1.5 rounded-lg text-xs font-semibold flex items-center space-x-1 transition-all ${
                viewMode === 'calendar' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              <CalendarDays size={14} />
              <span className="hidden sm:inline">Calendar</span>
            </button>
          </div>

          <button
            onClick={handleExportCsv}
            className="px-3.5 py-2 rounded-xl font-bold text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 flex items-center space-x-1.5 transition-colors shadow-md"
          >
            <Download size={15} />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900/60 p-4 rounded-2xl border border-slate-800">
        <div className="flex items-center space-x-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
          {['', 'PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED'].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                statusFilter === st ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800'
              }`}
            >
              {st || 'All Bookings'}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <input
            type="text"
            placeholder="Search by client or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' ? (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-16 text-slate-500 text-xs">
              No bookings matching your search criteria.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-300">
                <thead className="bg-slate-950 text-slate-400 uppercase font-bold text-[10px] tracking-wider border-b border-slate-800">
                  <tr>
                    <th className="px-5 py-4">Client</th>
                    <th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Service</th>
                    <th className="px-5 py-4">Requested Date</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/80 font-medium">
                  {bookings.map((b) => (
                    <tr key={b.id} className="hover:bg-slate-800/40 transition-colors">
                      <td className="px-5 py-4">
                        <div className="font-bold text-white text-sm">{b.fullName}</div>
                        {b.company && <div className="text-[11px] text-slate-400">{b.company}</div>}
                      </td>
                      <td className="px-5 py-4 space-y-0.5">
                        <div className="text-blue-400">{b.email}</div>
                        {b.phone && <div className="text-slate-400 text-[11px]">{b.phone}</div>}
                      </td>
                      <td className="px-5 py-4">
                        <span className="px-2.5 py-1 rounded-md bg-slate-950 border border-slate-800 text-[11px] font-semibold text-slate-300">
                          {b.serviceRequested || 'General Consultation'}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-mono text-[11px] text-slate-400">
                        {b.preferredDate ? new Date(b.preferredDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Flexible'}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          b.status === 'CONFIRMED'
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : b.status === 'COMPLETED'
                            ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                            : b.status === 'CANCELLED'
                            ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                            : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        }`}>
                          <span>{b.status}</span>
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center space-x-1.5">
                          <button
                            onClick={() => setSelectedBooking(b)}
                            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                            title="View Full Details"
                          >
                            <Eye size={14} />
                          </button>
                          {b.status === 'PENDING' && (
                            <button
                              onClick={() => updateStatus(b.id, 'CONFIRMED')}
                              className="px-2.5 py-1 rounded-lg bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600 hover:text-white font-bold text-[11px] transition-all"
                            >
                              Confirm
                            </button>
                          )}
                          {b.status === 'CONFIRMED' && (
                            <button
                              onClick={() => updateStatus(b.id, 'COMPLETED')}
                              className="px-2.5 py-1 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white font-bold text-[11px] transition-all"
                            >
                              Complete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* Calendar Schedule Grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {bookings.map((b) => (
            <div
              key={b.id}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg flex flex-col justify-between space-y-3"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">
                    {b.serviceRequested || 'Consultation'}
                  </span>
                  <span className="text-[11px] font-mono text-slate-400">
                    {b.preferredDate ? new Date(b.preferredDate).toLocaleDateString() : 'TBD'}
                  </span>
                </div>
                <h3 className="font-bold text-sm text-white">{b.fullName}</h3>
                <p className="text-xs text-slate-400">{b.company || 'Direct Client'}</p>
                {b.notes && (
                  <p className="text-xs text-slate-300 line-clamp-2 mt-2 bg-slate-950/60 p-2 rounded-lg border border-slate-800/60">
                    &quot;{b.notes}&quot;
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-slate-800 text-xs">
                <button
                  onClick={() => setSelectedBooking(b)}
                  className="text-blue-400 hover:text-blue-300 font-semibold"
                >
                  View Details
                </button>
                <select
                  value={b.status}
                  onChange={(e) => updateStatus(b.id, e.target.value)}
                  className="bg-slate-950 border border-slate-800 text-xs text-white rounded-lg px-2 py-1 focus:outline-none"
                >
                  <option value="PENDING">PENDING</option>
                  <option value="CONFIRMED">CONFIRMED</option>
                  <option value="COMPLETED">COMPLETED</option>
                  <option value="CANCELLED">CANCELLED</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h2 className="text-base font-bold text-white">Booking Details</h2>
              <button
                onClick={() => setSelectedBooking(null)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-slate-400 block mb-0.5">Client Full Name</span>
                  <span className="text-white font-bold text-sm">{selectedBooking.fullName}</span>
                </div>
                <div>
                  <span className="text-slate-400 block mb-0.5">Company</span>
                  <span className="text-white font-bold">{selectedBooking.company || 'N/A'}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-slate-400 block mb-0.5">Email Address</span>
                  <a href={`mailto:${selectedBooking.email}`} className="text-blue-400 font-semibold hover:underline">
                    {selectedBooking.email}
                  </a>
                </div>
                <div>
                  <span className="text-slate-400 block mb-0.5">Phone</span>
                  <span className="text-white">{selectedBooking.phone || 'N/A'}</span>
                </div>
              </div>

              <div>
                <span className="text-slate-400 block mb-0.5">Service Requested</span>
                <span className="px-2.5 py-1 rounded bg-slate-950 border border-slate-800 text-white font-semibold inline-block">
                  {selectedBooking.serviceRequested || 'General Consultation'}
                </span>
              </div>

              {selectedBooking.notes && (
                <div>
                  <span className="text-slate-400 block mb-1">Client Consultation Notes</span>
                  <div className="bg-slate-950 border border-slate-800 p-3 rounded-xl text-slate-300 leading-relaxed">
                    {selectedBooking.notes}
                  </div>
                </div>
              )}

              <div>
                <span className="text-slate-400 block mb-1.5">Change Status</span>
                <div className="flex items-center space-x-2">
                  {['PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED'].map((st) => (
                    <button
                      key={st}
                      onClick={() => updateStatus(selectedBooking.id, st)}
                      className={`px-3 py-1.5 rounded-xl font-bold text-xs transition-all ${
                        selectedBooking.status === st
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-slate-800 text-slate-400 hover:text-white'
                      }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/40 flex justify-end">
              <button
                onClick={() => setSelectedBooking(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-800 text-slate-200 hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
